import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { Router, RouterModule } from '@angular/router';
import _ from 'lodash';
import { TranslatorService } from '../../../services/translator_service';
import { ApiQuery } from '../../../web-services/api/api.query';
import { ResponsiveService } from '../../../services/responsive.service';
import { MatDialog } from '@angular/material/dialog';
import { ResolveTicketModal } from '../../ticket-management/resolve-ticket-modal/resolve-ticket-modal';
import { Subject } from 'rxjs';
import { GoogleMapsLoaderService } from '../../../services/google-maps-loader.service';

@Component({
  selector: 'app-technician-ticket-management-map',
  imports: [GoogleMapsModule,MapMarker,MapInfoWindow,GoogleMap,RouterModule],
  templateUrl: './technician-ticket-management-map.html',
  styleUrl: './technician-ticket-management-map.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TechnicianTicketManagementMap {
  currentLang;
	translationsObj;
  isMobile: boolean;
	user_role_id = 0;

  technicianTicketMap: any = null;
  markersTechnicianTicketsArray = [];
  binDataTechnicianTickets:Array<object> = [];

  @ViewChildren(MapInfoWindow) infoWindows!: QueryList<MapInfoWindow>;
  @Output() markerSelected = new EventEmitter<object>();
  @Output() markerDeselected = new EventEmitter<void>();  
	private readonly destroy$ = new Subject<void>();
  binsToDisplay:Array<object> = [];

  mapOptions: google.maps.MapOptions = {
    streetViewControl : false,
    disableDefaultUI : false,
    fullscreenControl : true,
    zoomControl : false
  };

  mobileMapOptions: google.maps.MapOptions = {
    streetViewControl: true,
    disableDefaultUI : false,
    fullscreenControl : true,
    zoomControl : false
  };

  technicianTicketsArr:Array<object> = [];
  @Input() set technicianTicketsArray(technicianTicketsArray: any) {
    const openTickets = (technicianTicketsArray || []).filter(item => item.status == 1);
    if (this.sameTicketRows(this.technicianTicketsArr as any[], openTickets)) {
      return;
    }
    this.technicianTicketsArr = openTickets;
    if (this.technicianTicketMap) {
      this.renderTechnicianTicketMarkers();
    }
  }

  private sameTicketRows(current: any[], next: any[]): boolean {
    if (current === next) {
      return true;
    }
    if (current == null || next == null) {
      return false;
    }
    if (current.length != next.length) {
      return false;
    }
    for (let index = 0; index < current.length; index++) {
      if (current[index] !== next[index]) {
        return false;
      }
    }
    return true;
  }

  private renderTechnicianTicketMarkers(): void {
    this.setTechnicianTicketMarkersOnMap(this.technicianTicketsArr);
    this.loadAPIWrapperInternal(this.technicianTicketMap, this.markersToFit(this.markersTechnicianTicketsArray));
  }

  private markersToFit(markers: any[]): any[] {
    return markers.filter(marker => marker["visible"] == true && marker["bin_lat"] != null && marker["bin_lon"] != null);
  }

  constructor(private translator: TranslatorService,private responsiveService: ResponsiveService,private apiQuery:ApiQuery,
    private dialog: MatDialog,private router: Router,protected googleMapsLoader: GoogleMapsLoaderService) { 
    this.translator.currentLangEmitter$
		.subscribe(async value=>{						
		  this.translationsObj = await this.translator.getTranslation(value).toPromise()			 
		  this.currentLang = value;	
		})
  }

  ngOnInit() {
    this.onResize();
    this.responsiveService.checkWidth();

    // Ensure the Google Maps script is loaded before <google-map> renders,
    // otherwise the `google` namespace is undefined ("Namespace google not found").
    // The template gates the map on googleMapsLoader.ready() (a signal), which
    // updates this OnPush view automatically once the API is ready.
    this.googleMapsLoader.loadGoogleMaps();

    this.apiQuery.user$.subscribe(user => {
      if (!user) {
        return;
      }		
      this.user_role_id = user["user_role_id"];
    }); 
  }
  
  onResize() {
    this.responsiveService.getMobileStatus().subscribe(isMobile => {
      this.isMobile = isMobile;
    });
  }

  fullTechnicianTicketScreenchange(ev?){
    if (ev == 0 && document.fullscreenElement) {
      document.exitFullscreen();
    }
  }

  isNumber(val): boolean { return typeof val === 'number'; }

  openMarkerInfoWindow(marker: MapMarker, markerIndex: number) {
		this.infoWindows.forEach((infoWindow: MapInfoWindow, index: number ) => {
		  if (index === markerIndex) {
			infoWindow.open(marker);
		  } else {
			infoWindow.close();
		  }
		});
	}

  loadAPIWrapperInternal(map: any,bins) {
		if (!map) return;
		const userPosition = {
			lat: Number("32.085300"),
			lng: Number("34.781768")
		};
		if (bins.length > 0) {
			const bounds = new google.maps.LatLngBounds();
			bins.map(bin => {
				if(bin.bin_lat != 0 && bin.bin_lon != 0 && 
					bin.bin_lat != null && bin.bin_lon != null && 
					bin.bin_lat != '' && bin.bin_lon != ''){	
						bounds.extend({ lat: Number(bin.bin_lat), lng: Number(bin.bin_lon) });				
				}	
			});
			map.fitBounds(bounds);
		} else {
			map.panTo(userPosition);
		}
	}

  mapClick() {	
		this.infoWindows.forEach((infoWindow: MapInfoWindow ) => {
			infoWindow.close();
		});
		this.markersTechnicianTicketsArray.forEach((marker) => {
			if (marker.opacity) {
				marker.opacity = 1;
			}
		});
		this.markerDeselected.emit();
	}

  loadTechnicianTicketMap(map: any){
    this.technicianTicketMap = map;
    this.addYourLocationButton(this.technicianTicketMap,'center-technician');	
    this.binsToDisplay = this.technicianTicketsArr;
    this.binDataTechnicianTickets = this.binsToDisplay;
    this.renderTechnicianTicketMarkers();
  }

  mapBinsByCoordinates(bins) {
		const binsHash = {};
		_.each(bins, b => {
      if (binsHash[b.bin_lat+","+b.bin_lon] !== undefined) {
        binsHash[b.bin_lat+","+b.bin_lon].push(b);
      } else {
        binsHash[b.bin_lat+","+b.bin_lon] = [b];
      }
		});
		return binsHash;
	}

  BinToIcon(val){
    let icon = '';
    if(val.ticket_event_type_id == 1){
      if(val.priority_id == 1){
				icon = `../../../../assets/images/bins/checkOrRecplacement_PL.svg`;
      }else if(val.priority_id == 2){
        icon = `../../../../assets/images/bins/checkOrRecplacement_PM.svg`;
      }else{
        icon = `../../../../assets/images/bins/checkOrRecplacement_PH.svg`;
      }
    }else if(val.ticket_event_type_id == 3){
      if(val.priority_id == 1){
				icon = `../../../../assets/images/bins/installation_PL.svg`;
      }else if(val.priority_id == 2){
        icon = `../../../../assets/images/bins/installation_PM.svg`;
      }else{
        icon = `../../../../assets/images/bins/installation_PH.svg`;
      }
    }else if(val.ticket_event_type_id == 4){
      if(val.priority_id == 1){
				icon = `../../../../assets/images/bins/cancellation_PL.svg`;
      }else if(val.priority_id == 2){
        icon = `../../../../assets/images/bins/cancellation_PM.svg`;
      }else{
        icon = `../../../../assets/images/bins/cancellation_PH.svg`;
      }
    }
    return icon;
  }

  private technicianClusterToIcon(cluster){
    let markersAroundIcon = '';
    if(cluster.length == 2){
      markersAroundIcon = '1';
    }else if(cluster.length == 3){
      markersAroundIcon = '2';
    }else if(cluster.length == 4){
      markersAroundIcon = '3';
    }else{
      markersAroundIcon = '4';
    }

    let icon = '';
    if(cluster[0].ticket_event_type_id == 1){
      icon = `../../../../assets/images/bins/all/technicianCheckOrRecplacementCluster${markersAroundIcon}.svg`;
    }else if(cluster[0].ticket_event_type_id == 3){
      icon = `../../../../assets/images/bins/all/technicianInstallationCluster${markersAroundIcon}.svg`;
    }else if(cluster[0].ticket_event_type_id == 4){
      icon = `../../../../assets/images/bins/all/technicianCancellationCluster${markersAroundIcon}.svg`;
    }
    return icon;
  }

  setTechnicianTicketMarkersOnMap(bins){
    this.markersTechnicianTicketsArray = [];
    let binsHash = this.mapBinsByCoordinates(bins);		
		_.forOwn(binsHash, (value :any, key) => {	
			if(value.length > 1){
				const icon = this.technicianClusterToIcon(value);
				const marker = {
          item:value,
          icon: icon,
          opacity: 1,
          lat: Number(value[0].bin_lat),
          lng: Number(value[0].bin_lon),
          bin_lon: Number(value[0].bin_lon),
          bin_lat: Number(value[0].bin_lat),
          visible: true,
          draggable: false,
          isInfoWindowOpen: false,
          bin_id: value[0].bin_id,
          bin_name:value[0].bin_name,
          bin_address: value[0].bin_address,
          ticket_event_type_id: value[0].ticket_event_type_id,
          eventTypeEn: value[0].eventTypeEn,
          eventTypeHe: value[0].eventTypeHe,
          partOfCluster: true
				};
        this.markersTechnicianTicketsArray.push(marker);
			}else{
				value.forEach((element, index, array) => {
					const b = element;						
					const icon = this.BinToIcon(b);
					
					const marker = {
						icon: icon,
						opacity: 1,
						lat: Number(b.bin_lat),
						lng: Number(b.bin_lon),				
            bin_lon: Number(b.bin_lon),
            bin_lat: Number(b.bin_lat),	
						visible: true,
            draggable: false,
            isInfoWindowOpen : false,
            bin_id: b.bin_id,
            bin_name:b.bin_name,
            bin_address: b.bin_address,
            ticket_event_type_id: b.ticket_event_type_id,
            priority_id: b.priority_id,
            eventTypeEn: b.eventTypeEn,
            eventTypeHe: b.eventTypeHe,
            eventCauseEn: b.eventCauseEn,
            eventCauseHe: b.eventCauseHe,
            priorityEn: b.priorityEn,
            priorityHe: b.priorityHe,
            site_id: b.site_id,
            site_name: b.site_name,
            ticket_id: b.ticket_id,
            partOfCluster: false,
            comment: b.comment
					};	
          this.markersTechnicianTicketsArray.push(marker);
				});
			}
		});
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition( pos => {  
        const relevantMarker = _.filter(this.markersTechnicianTicketsArray, (res) => {      
          return res["id"] == "startMarkerTechnician";
        });  
        if(relevantMarker.length == 0){
          const startMarker = new google.maps.Marker({
            position: {
              lat: Number(pos.coords.latitude),
              lng: Number(pos.coords.longitude)
            },
            icon: '../../../../assets/images/workplans-page/user-location.svg',
            map: this.technicianTicketMap
          });
          startMarker["id"] = "startMarkerTechnician";
          this.markersTechnicianTicketsArray.push(startMarker);
        }
      }, error => {
      },{maximumAge:10000, timeout:5000, enableHighAccuracy: true});
    }
  }

  addYourLocationButton(map,id) {
		let controlDiv = document.createElement('div');
		let firstChild = document.createElement('button');
		firstChild.type = 'button';
		firstChild.style.backgroundColor = '#fff';
		firstChild.style.border = 'none';
		firstChild.style.outline = 'none';
		firstChild.style.width = '40px';
		firstChild.style.height = '40px';
		firstChild.style.borderRadius = '2px';
		firstChild.style.boxShadow = '0 1px 4px rgba(0,0,0,0.3)';
		firstChild.style.cursor = 'pointer';
		firstChild.style.marginRight = '10px';
		firstChild.style.padding = '0px';
		firstChild.title = 'Your Location';
		controlDiv.appendChild(firstChild);
		
		let secondChild = document.createElement('div');
		secondChild.style.margin = 'calc(50% - 18px / 2)';
		secondChild.style.width = '18px';
		secondChild.style.height = '18px';
		secondChild.style.backgroundImage = 'url(https://maps.gstatic.com/tactile/mylocation/mylocation-sprite-1x.png)';
		secondChild.style.backgroundSize = '180px 18px';
		secondChild.style.backgroundPosition = '0px 0px';
		secondChild.style.backgroundRepeat = 'no-repeat';
		secondChild.id = id;
		firstChild.appendChild(secondChild);
		
		google.maps.event.addListener(map, 'dragend', function() {
			document.getElementById(id).style.backgroundPosition = '0px 0px';
		});

		firstChild.addEventListener('click', ()=> {
			var imgX = '0';
			var animationInterval = setInterval(function(){
				if(imgX == '-18') imgX = '0';
				else imgX = '-18';
				document.getElementById(id).style.backgroundPosition = imgX+'px 0px';
			}, 500);
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition( pos => {        
				map.setCenter({lat: pos.coords.latitude, lng: pos.coords.longitude});
        map.setZoom(15);
				clearInterval(animationInterval);
				document.getElementById(id).style.backgroundPosition = '-144px 0px';
				}, error => {
				},{maximumAge:10000, timeout:5000, enableHighAccuracy: true});
			}else{
				clearInterval(animationInterval);
				document.getElementById(id).style.backgroundPosition = '0px 0px';
			}
		});
		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(controlDiv);
	}

  clickedMarker(bin) {	
    this.markersTechnicianTicketsArray.forEach((marker) => {
      if (marker.opacity) {
        marker.opacity = marker === bin ? 1 : 0.3;
      }
    });
    this.markerSelected.emit(bin);
            
    let content;
    if(!bin.partOfCluster){
      let closeEventPopUp = document.createElement('div');
      if(this.isMobile){
        closeEventPopUp.className = 'mobile-tooltip-action-btn';
      }else{
        closeEventPopUp.className = 'tooltip-action-btn';
      }
      closeEventPopUp.setAttribute("name",bin.ticket_id);
      closeEventPopUp.id = bin.ticket_id;
      closeEventPopUp.innerHTML = this.translationsObj.TICKET_MANAGEMENT.CLOSE;
  
      closeEventPopUp.addEventListener('click', ()=> {
        let binChosen = [];
        let class_name = '';
        if(this.isMobile){
          class_name = 'popup-container-mobile';
        }else{
          class_name = 'bulkBins-container';
        }

        binChosen = this.technicianTicketsArr.filter(item => item["ticket_id"] == bin["ticket_id"]);

        if(binChosen.length > 0){
          this.dialog.closeAll();
          this.fullTechnicianTicketScreenchange(0);
          this.dialog.open(ResolveTicketModal, { panelClass: class_name , data: {chosenTicket : binChosen[0]} });
        }
      });

      let wazeLink = document.createElement('a');
      wazeLink.className = 'tooltip-waze';
      wazeLink.setAttribute("href",`https://waze.com/ul?ll=${bin.lat},${bin.lng}`);
  
      let wazeImg = document.createElement('img');
      wazeImg.setAttribute("src","assets/images/dashboard/waze.svg");
      wazeLink.appendChild(wazeImg);
  
      content = document.createElement('div');
      content.className = 'iw-content';
      content.setAttribute("style","padding-left:unset;padding-right:unset;");
  
      let tooltipId = document.createElement('div');
      tooltipId.className = 'tooltip-id';
  
      let tooltipValue = document.createElement('div');
      tooltipValue.className = 'tooltip-value';
  
      let disp = document.createElement('div');
      disp.className = 'disp space-between';
  
      let dispGap = document.createElement('div');
      dispGap.className = 'disp gap';
  
      let dispHeaderTooltip = document.createElement('div');
      dispHeaderTooltip.className = 'disp header-tooltip';
  
      let span1 = document.createElement('span');
      span1.innerHTML = this.translationsObj.LIST_COLUMNS.ID;
  
      let span2 = document.createElement('span');
      span2.innerHTML = bin.ticket_id;
      dispHeaderTooltip.appendChild(span1);
      dispHeaderTooltip.appendChild(span2);
  
      let dispHeaderTooltip2 = document.createElement('div');
      dispHeaderTooltip2.className = 'disp header-tooltip';
  
      let span3 = document.createElement('span');
      span3.innerHTML = bin.ticket_event_type_id != 3 ? '|' : '';
      
      let buttonToBinLiveInfo = document.createElement('button');
      buttonToBinLiveInfo.className = 'binNameHref-truckMap tooltip-value bin-name binInfo_btn';
      buttonToBinLiveInfo.addEventListener("click", (e) => {
        this.router.navigate(['/binLiveInfoPage'], { queryParams: { pageName: 'dashboard', binId: bin.bin_id } });
      });
      buttonToBinLiveInfo.innerHTML = bin.ticket_event_type_id != 3 ? bin.bin_name : '';
      
      dispHeaderTooltip2.appendChild(span3);
      dispHeaderTooltip2.appendChild(buttonToBinLiveInfo);
      dispGap.appendChild(dispHeaderTooltip);
      dispGap.appendChild(dispHeaderTooltip2);
      disp.appendChild(dispGap);
      if(!this.isMobile && this.user_role_id != 2 && this.user_role_id != 3){
        disp.appendChild(closeEventPopUp);
      }
      tooltipValue.appendChild(disp);
      tooltipId.appendChild(tooltipValue);
      
      let tooltipId2 = document.createElement('div');
      tooltipId2.className = 'tooltip-id border-bottom';
      
      let tooltipAddress = document.createElement('div');
      tooltipAddress.className = 'tooltip-address address-waze gap';
      tooltipAddress.setAttribute("title",bin.bin_address);
      tooltipAddress.innerHTML = bin.bin_address + wazeLink.outerHTML;
      
      tooltipId2.appendChild(tooltipAddress);
      tooltipId.appendChild(tooltipId2);
      content.appendChild(tooltipId);
      
      let tooltipDividerMargin = document.createElement('div');
      tooltipDividerMargin.className = 'tooltip-divider-margin';
      
      let disp2 = document.createElement('div');
      disp2.className = 'disp space-between';
      
      let titleInfo = document.createElement('span');
      titleInfo.className = 'title-info';
      titleInfo.innerHTML = this.translationsObj.TICKET_MANAGEMENT.EVENT_CAUSE;
      
      let valueInfo = document.createElement('span');
      valueInfo.className = 'value-info';
      valueInfo.innerHTML = this.currentLang == 'iw' ? bin.eventCauseHe : bin.eventCauseEn;
      
      disp2.appendChild(titleInfo);
      disp2.appendChild(valueInfo);
      
      let disp3 = document.createElement('div');
      disp3.className = 'disp space-between';
      
      let titleInfo2 = document.createElement('span');
      titleInfo2.className = 'title-info';
      titleInfo2.innerHTML = this.translationsObj.TICKET_MANAGEMENT.PRIORITY;
      
      let valueInfo2 = document.createElement('span');
      valueInfo2.className = 'value-info';
      valueInfo2.innerHTML = this.currentLang == 'iw' ? bin.priorityHe : bin.priorityEn;
      
      disp3.appendChild(titleInfo2);
      disp3.appendChild(valueInfo2);
      
      tooltipDividerMargin.appendChild(disp2);
      tooltipDividerMargin.appendChild(disp3);
      if(this.isMobile && this.user_role_id != 2 && this.user_role_id != 3){
        tooltipDividerMargin.appendChild(closeEventPopUp);
      }
      content.appendChild(tooltipDividerMargin);
    }else{      
			let binTooltips = '';
      for(let i=0; i < bin.item.length; i++){
        let closeEventPopUp = document.createElement('div');
        if(this.isMobile){
          closeEventPopUp.className = 'mobile-tooltip-action-btn';
        }else{
          closeEventPopUp.className = 'tooltip-action-btn';
        }
        closeEventPopUp.setAttribute("name",bin.item[i].ticket_id);
        closeEventPopUp.id = bin.item[i].ticket_id;
        closeEventPopUp.innerHTML = this.translationsObj.TICKET_MANAGEMENT.CLOSE;

        document.addEventListener('click',(e)=>{
          if(e.target && e.target['className'] == 'tooltip-action-btn' || e.target['className'] == 'mobile-tooltip-action-btn'){
            let binChosen = [];
            let class_name = '';
            if(this.isMobile){
              class_name = 'popup-container-mobile';
            }else{
              class_name = 'bulkBins-container';
            }

            binChosen = this.technicianTicketsArr.filter(item => item["ticket_id"] == e.target['id']);

            if(binChosen.length > 0){
              this.dialog.closeAll();
              this.fullTechnicianTicketScreenchange(0);
              this.dialog.open(ResolveTicketModal, { panelClass: class_name , data: {chosenTicket : binChosen[0]} });
            }
          }
        });

        let wazeLink = document.createElement('a');
        wazeLink.className = 'tooltip-waze';
        wazeLink.setAttribute("href",`https://waze.com/ul?ll=${bin.lat},${bin.lng}`);
    
        let wazeImg = document.createElement('img');
        wazeImg.setAttribute("src","assets/images/dashboard/waze.svg");
        wazeLink.appendChild(wazeImg);
    
        let carouselItem = document.createElement('div');
        carouselItem.className = 'carousel-item';
        if(i == 0){
          carouselItem.className += ' active';
        }

        content = document.createElement('div');
        content.className = 'iw-content';
        content.setAttribute("style","padding-left:unset;padding-right:unset;");
    
        let tooltipId = document.createElement('div');
        tooltipId.className = 'tooltip-id';
    
        let tooltipValue = document.createElement('div');
        tooltipValue.className = 'tooltip-value';
    
        let disp = document.createElement('div');
        disp.className = 'disp space-between';
    
        let dispGap = document.createElement('div');
        dispGap.className = 'disp gap';
    
        let dispHeaderTooltip = document.createElement('div');
        dispHeaderTooltip.className = 'disp header-tooltip';
    
        let span1 = document.createElement('span');
        span1.innerHTML = this.translationsObj.LIST_COLUMNS.ID;
    
        let span2 = document.createElement('span');
        span2.innerHTML = bin.item[i].ticket_id;
        dispHeaderTooltip.appendChild(span1);
        dispHeaderTooltip.appendChild(span2);
    
        let dispHeaderTooltip2 = document.createElement('div');
        dispHeaderTooltip2.className = 'disp header-tooltip';
    
        let span3 = document.createElement('span');
        span3.innerHTML = bin.item[i].ticket_event_type_id != 3 ? '|' : '';
        
        let buttonToBinLiveInfo = document.createElement('a');
        buttonToBinLiveInfo.setAttribute('class','binNameHref-truckMap tooltip-value bin-name binInfo_btn');
        buttonToBinLiveInfo.setAttribute('href',`/binLiveInfoPage?pageName=dashboard&binId=${bin.item[i].bin_id}`);
        buttonToBinLiveInfo.innerHTML = bin.item[i].ticket_event_type_id != 3 ? bin.item[i].bin_name : '';
        
        dispHeaderTooltip2.appendChild(span3);
        dispHeaderTooltip2.appendChild(buttonToBinLiveInfo);
        dispGap.appendChild(dispHeaderTooltip);
        dispGap.appendChild(dispHeaderTooltip2);
        disp.appendChild(dispGap);
        if(!this.isMobile && this.user_role_id != 2 && this.user_role_id != 3){
          disp.appendChild(closeEventPopUp);
        }
        tooltipValue.appendChild(disp);
        tooltipId.appendChild(tooltipValue);
        
        let tooltipId2 = document.createElement('div');
        tooltipId2.className = 'tooltip-id border-bottom';
        
        let tooltipAddress = document.createElement('div');
        tooltipAddress.className = 'tooltip-address address-waze gap';
        tooltipAddress.setAttribute("title",bin.item[i].bin_address);
        tooltipAddress.innerHTML = bin.item[i].bin_address + wazeLink.outerHTML;
        
        tooltipId2.appendChild(tooltipAddress);
        tooltipId.appendChild(tooltipId2);
        content.appendChild(tooltipId);
        
        let tooltipDividerMargin = document.createElement('div');
        tooltipDividerMargin.className = 'tooltip-divider-margin';
        
        let disp2 = document.createElement('div');
        disp2.className = 'disp space-between';
        
        let titleInfo = document.createElement('span');
        titleInfo.className = 'title-info';
        titleInfo.innerHTML = this.translationsObj.TICKET_MANAGEMENT.EVENT_CAUSE;
        
        let valueInfo = document.createElement('span');
        valueInfo.className = 'value-info';
        valueInfo.innerHTML = this.currentLang == 'iw' ? bin.item[i].eventCauseHe : bin.item[i].eventCauseEn;
        
        disp2.appendChild(titleInfo);
        disp2.appendChild(valueInfo);
        
        let disp3 = document.createElement('div');
        disp3.className = 'disp space-between';
        
        let titleInfo2 = document.createElement('span');
        titleInfo2.className = 'title-info';
        titleInfo2.innerHTML = this.translationsObj.TICKET_MANAGEMENT.PRIORITY;
        
        let valueInfo2 = document.createElement('span');
        valueInfo2.className = 'value-info';
        valueInfo2.innerHTML = this.currentLang == 'iw' ? bin.item[i].priorityHe : bin.item[i].priorityEn;
        
        disp3.appendChild(titleInfo2);
        disp3.appendChild(valueInfo2);
        
        tooltipDividerMargin.appendChild(disp2);
        tooltipDividerMargin.appendChild(disp3);
        if(this.isMobile && this.user_role_id != 2 && this.user_role_id != 3){
          tooltipDividerMargin.appendChild(closeEventPopUp);
        }
        content.appendChild(tooltipDividerMargin);
        carouselItem.appendChild(content);
        binTooltips += carouselItem.outerHTML;
      }

      let prevNextArrow = document.createElement('button');
      prevNextArrow.setAttribute("data-bs-target","#carouselExampleFade");
      prevNextArrow.setAttribute("data-bs-slide","prev");
      prevNextArrow.className = 'prevNextArrow carousel-control';

      let imgPrev = document.createElement('img');
      imgPrev.src = "assets/images/dashboard/pagination_arrow.png";
      imgPrev.alt = "";
      imgPrev.className = this.currentLang != 'iw' ? "rotation" : "";

      let spanPrev = document.createElement('span');
      spanPrev.className = 'sr-only';
      spanPrev.innerHTML = '';

      prevNextArrow.appendChild(imgPrev);
      prevNextArrow.appendChild(spanPrev);

      let span = document.createElement('span');
      span.style.position = 'relative';
      span.style.bottom = '3px';
      span.style.padding = '0px 8px';
      span.innerHTML = '|';

      let nextArrow = document.createElement('button');
      nextArrow.setAttribute("data-bs-target","#carouselExampleFade");
      nextArrow.setAttribute("data-bs-slide","next");
      nextArrow.className = 'prevNextArrow carousel-control';

      let imgNext = document.createElement('img');
      imgNext.src = "assets/images/dashboard/pagination_arrow.png";
      imgNext.alt = "";
      imgNext.className = this.currentLang == 'iw' ? "rotation" : "";

      let spanNext = document.createElement('span');
      spanNext.className = 'sr-only';
      spanNext.innerHTML = '';

      nextArrow.appendChild(imgNext);
      nextArrow.appendChild(spanNext);

      let carousel = document.createElement('div');
      carousel.id = "carouselExampleFade";
      carousel.className = 'carousel slide carousel-fade';
      carousel.setAttribute("data-ride","carousel");
      carousel.style.top = '9px';

      let carouselInnerFrags = document.createElement('div');
      carouselInnerFrags.id = "carousel-inner-frags";
      carouselInnerFrags.className = 'carousel-inner';
      carouselInnerFrags.style.overflow = 'unset';

      carouselInnerFrags.innerHTML = binTooltips;
      carousel.appendChild(carouselInnerFrags);

      content = document.createElement('div');
      content.appendChild(prevNextArrow);
      content.appendChild(span);
      content.appendChild(nextArrow);
      content.appendChild(carousel);
    }
    
    let node;
    node = document.createElement("div");
    node.id = "infoWindow-popup";
    node.className = 'infoWindow-class';
    node.appendChild(content);
    
    setTimeout(() => {
      this.checkIfInfoWindowExist();
      document.getElementById("singleBinInfo").append(node);
    }, 100);
	}

  checkIfInfoWindowExist(){
		if(document.getElementById('infoWindow-popup')){
			document.getElementById('infoWindow-popup').remove();
		}
	}
}
